import React, { useMemo } from 'react';
import { Grid2 as Grid } from '@mui/material';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  DashboardProject,
  getProjectManager,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { LinkField } from 'framework/ui/field';
import { PLetterIcon, ALetterIcon, Tooltip } from 'framework/ui/mui-builder';
import { orderExternalResources } from '../List/orderExternalResources';
import ProjectExternalResources from './ProjectExternalResources';
import { EmployeeLinkFieldWithNameLoading, useIsEmployeeProfileAccessible } from 'domain/shared/employee';
import { Features } from 'infrastructure/featureToggle';
import useFeatureToggle from 'framework/featureToggle';
import { getControlIssues } from '../../shared/mainProjectSection/controlIssues';
import ControlIssueIcons from '../../shared/mainProjectSection/controlIssueIcons';

const ProjectItemCollapsed = ({ project }: { project: DashboardProject }) => {
  const { getControlsUrl, getOutcomesUrl, getProjectTeamUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();
  const { isFeatureEnabled } = useFeatureToggle();
  const controlIssues = getControlIssues(project);
  const projectManager = getProjectManager(project);
  const accountManager = project.accountData?.ownerAlias;
  const isProjectManagerAccessible = useIsEmployeeProfileAccessible(projectManager);
  const isAccountManagerAccessible = useIsEmployeeProfileAccessible(accountManager);

  const externalResources = useMemo(() => {
    const resources = project?.resourcesData?.externalResources || project?.externalResources;
    return orderExternalResources(resources);
  }, [project?.resourcesData?.externalResources, project?.externalResources]);

  return (
    <Grid
      container
      className="dashboard-item-content"
      columns={12}
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: '100%!important' }}
    >
      <Grid size={2.5}>
        <LinkField
          isInternal
          linkText={project.projectName || project.name}
          value={
            isFeatureEnabled(Features.CONTROLS) &&
            isAccessGranted(ACCESS_INFO_SECTIONS.CONTROL_PLANE, ACCESS_INFO_ACTIONS.VIEW)
              ? getControlsUrl(project.id)
              : isAccessGranted(ACCESS_INFO_SECTIONS.BUSINESS_OUTCOME, ACCESS_INFO_ACTIONS.VIEW)
                ? getOutcomesUrl(project.id)
                : getProjectTeamUrl(project.id)
          }
        />
      </Grid>
      <Grid size={2} display="flex" flexDirection="row" alignItems="center" gap={1}>
        {projectManager && (
          <>
            <Tooltip title="Project manager">
              <span style={{ alignItems: 'center', display: 'flex' }}>
                <PLetterIcon sx={{ fontSize: '1rem' }} />
              </span>
            </Tooltip>
            <EmployeeLinkFieldWithNameLoading
              ignoredNameParts={['middleName', 'maidenName']}
              value={projectManager}
              isLink={isProjectManagerAccessible}
              isOpenInNewTab
            />
          </>
        )}
      </Grid>
      <Grid size={2} display="flex" flexDirection="row" alignItems="center" gap={1}>
        {accountManager && (
          <>
            <Tooltip title="Account manager">
              <span style={{ alignItems: 'center', display: 'flex' }}>
                <ALetterIcon sx={{ fontSize: '1rem' }} />
              </span>
            </Tooltip>
            <EmployeeLinkFieldWithNameLoading
              ignoredNameParts={['middleName', 'maidenName']}
              value={accountManager}
              isLink={isAccountManagerAccessible}
              isOpenInNewTab
            />
          </>
        )}
      </Grid>
      <Grid size={3}>
        {isAccessGranted(ACCESS_INFO_SECTIONS.HEALTH, ACCESS_INFO_ACTIONS.VIEW) && (
          <ControlIssueIcons issues={controlIssues} variant="icons" />
        )}
      </Grid>
      <Grid size={2.5}>
        <ProjectExternalResources resources={externalResources} />
      </Grid>
    </Grid>
  );
};

export default ProjectItemCollapsed;
